import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Search, ShieldAlert, BookOpen } from "lucide-react";
import { api } from "../lib/api";
import { ProductCard } from "../components/store/ProductCard";
import { Reveal } from "../components/store/Reveal";

const CATS = ["Tutti", "Box Degustazione", "Primi Piatti Pronti", "Secondi Piatti Pronti", "Contorni", "Fritti", "Kris", "Dolci", "Sughi Pronti", "Gluten Fit"];
const ALLERGEN_KEYWORDS = [
  { key: "LATTE", match: ["LATTE"] },
  { key: "UOVA", match: ["UOVO", "UOVA"] },
  { key: "SEDANO", match: ["SEDANO"] },
  { key: "SOLFITI", match: ["SOLFITI"] },
  { key: "SOIA", match: ["SOIA"] },
  { key: "SENAPE", match: ["SENAPE"] },
  { key: "PESCE", match: ["PESCE", "CERNIA", "SGOMBRO", "SALMONE", "TONNO", "SPADA", "ACCIUG"] },
  { key: "CROSTACEI", match: ["CROSTACEI", "GAMBER"] },
  { key: "MOLLUSCHI", match: ["MOLLUSCHI", "VONGOL", "COZZE", "CALAMAR", "POLPO"] },
  { key: "FRUTTA A GUSCIO", match: ["PISTACCHIO", "FRUTTA A GUSCIO", "NOCI", "MANDORL", "PINOLI"] },
];

const allergenBlob = (p) => `${(p.allergens || []).join(" ")} ${p.traces || ""} ${p.ingredients || ""}`.toUpperCase();

export default function Products() {
  const [params, setParams] = useSearchParams();
  const cat = params.get("cat") || "Tutti";
  const [products, setProducts] = useState([]);
  const [query, setQuery] = useState("");
  const [excluded, setExcluded] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).finally(() => setLoading(false));
  }, []);

  const availableAllergens = useMemo(() => {
    const blobs = products.map(allergenBlob);
    return ALLERGEN_KEYWORDS.filter((a) => blobs.some((b) => a.match.some((m) => b.includes(m))));
  }, [products]);

  const toggleAllergen = (key) =>
    setExcluded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const filtered = useMemo(
    () =>
      products.filter((p) => {
        if (cat !== "Tutti" && !(p.categories?.includes(cat) || p.category === cat)) return false;
        if (!p.name.toLowerCase().includes(query.toLowerCase())) return false;
        if (excluded.length > 0) {
          const blob = allergenBlob(p);
          const hit = excluded.some((key) =>
            ALLERGEN_KEYWORDS.find((a) => a.key === key).match.some((m) => blob.includes(m))
          );
          if (hit) return false;
        }
        return true;
      }),
    [products, cat, query, excluded]
  );

  return (
    <div data-testid="products-page" className="max-w-7xl mx-auto px-4 sm:px-8 py-12 lg:py-16">
      <Reveal>
        <p className="font-mono-accent text-xs uppercase tracking-[0.3em] text-[#2E8A57] mb-3">Il menu completo</p>
        <h1 className="font-serif-display font-bold text-4xl sm:text-5xl tracking-tight text-[#1C1917]">Tutti i piatti</h1>
        <p className="mt-4 text-[#44403C] max-w-lg">Ingredienti reali, peso e conservazione dal produttore. Ordine minimo 100€, spedizione refrigerata in tutta Italia.</p>
        <Link
          to="/libro"
          data-testid="open-flipbook-btn"
          className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm font-semibold border border-[#2E8A57] text-[#2E8A57] hover:bg-[#2E8A57] hover:text-[#FDFBF7] transition-colors duration-300"
        >
          <BookOpen className="w-4 h-4" /> Sfoglia il menu come libro 3D
        </Link>
      </Reveal>

      <div className="mt-10 flex flex-col md:flex-row gap-4 md:items-center justify-between">
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`category-tab-${c.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setParams(c === "Tutti" ? {} : { cat: c })}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 border ${
                cat === c ? "bg-[#2E8A57] text-[#FDFBF7] border-[#2E8A57]" : "border-[#E7E0D8] text-[#44403C] hover:border-[#2E8A57] hover:text-[#2E8A57]"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="relative md:w-72">
          <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-[#78716C]" />
          <input
            data-testid="product-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Cerca un piatto..."
            className="w-full rounded-full border border-[#E7E0D8] bg-white pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#2E8A57] transition-colors"
          />
        </div>
      </div>

      {availableAllergens.length > 0 && (
        <div className="mt-6 rounded-2xl border border-[#E7E0D8] bg-white p-5" data-testid="allergen-filter">
          <p className="font-mono-accent text-[10px] uppercase tracking-[0.25em] text-[#2E8A57] mb-3 flex items-center gap-2">
            <ShieldAlert className="w-3.5 h-3.5" /> Escludi allergeni
          </p>
          <div className="flex flex-wrap gap-2">
            {availableAllergens.map((a) => (
              <button
                key={a.key}
                data-testid={`allergen-chip-${a.key.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={() => toggleAllergen(a.key)}
                className={`px-4 py-2 rounded-full text-xs font-semibold border transition-all duration-200 ${
                  excluded.includes(a.key)
                    ? "bg-[#2E8A57] text-[#FDFBF7] border-[#2E8A57] line-through"
                    : "border-[#E7E0D8] text-[#44403C] hover:border-[#2E8A57] hover:text-[#2E8A57]"
                }`}
              >
                {a.key}
              </button>
            ))}
          </div>
          <p className="mt-3 text-[11px] text-[#78716C]">
            Nascondiamo i piatti che dichiarano gli allergeni selezionati (anche in tracce). Verifica sempre gli ingredienti sulla scheda prodotto.
          </p>
        </div>
      )}

      {renderProducts({ loading, filtered })}
    </div>
  );
}

function renderProducts({ loading, filtered }) {
  if (loading) return <p className="mt-16 text-center text-[#78716C]">Caricamento del menu...</p>;
  if (filtered.length === 0)
    return <p data-testid="no-products-message" className="mt-16 text-center text-[#78716C]">Nessun piatto trovato.</p>;
  return (
    <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
      {filtered.map((p, i) => (
        <Reveal key={p.slug} delay={(i % 4) * 0.06} y={24}>
          <ProductCard product={p} />
        </Reveal>
      ))}
    </div>
  );
}
